import axios from "axios";
import { baseApi } from "@/core/api/baseApi";
import { ApiConfig } from "@/features/auth/infrastructure/config/apiConfig";
import {
  normalizeOtpPreference,
  type OtpPreference,
} from "@/features/auth/domain/constants/otpConstants";
import type {
  UserSettings,
  UserSettingsSnapshot,
} from "../../domain/entities/UserSettings";
import { readTotpEnabledFromRecord } from "@/features/auth/domain/utils/readTotpEnabled";
import { getApiErrorMessage } from "@/features/auth/infrastructure/api/apiResponseUtils";
import type {
  UpdateUserSettingsDTO,
  UserSettingsDataDTO,
  UserSettingsResponseDTO,
} from "../../domain/dto/UserSettingsDTO";

const mapUserSettingsSnapshot = (
  data: UserSettingsDataDTO,
): UserSettingsSnapshot => ({
  otpPreference: normalizeOtpPreference(data.otp_preference) ?? undefined,
  isTotpEnabled:
    readTotpEnabledFromRecord(data as Record<string, unknown>) ?? false,
});

const mapUserSettings = (data: UserSettingsDataDTO): UserSettings => ({
  ...mapUserSettingsSnapshot(data),
  // Only returned for institute admins; leave it unset otherwise so the
  // Data Sharing Control stays hidden instead of defaulting to "sharing on".
  e2eEncryptionEnabled:
    typeof data.e2e_encryption_enabled === "boolean"
      ? data.e2e_encryption_enabled
      : undefined,
  webLanguage: data.web_language
    ? {
        id: data.web_language.id,
        code: data.web_language.code,
        name: data.web_language.name,
      }
    : undefined,
});

export const fetchUserSettings = async (
  token: string,
): Promise<UserSettingsSnapshot> => {
  const response = await axios.get<UserSettingsResponseDTO>(
    ApiConfig.USER_SETTINGS,
    { headers: { Authorization: `Bearer ${token}` } },
  );

  return mapUserSettingsSnapshot(response.data.data);
};

export const patchOtpPreference = async (
  accessToken: string,
  otpPreference: OtpPreference,
): Promise<void> => {
  const body: UpdateUserSettingsDTO = { otp_preference: otpPreference };
  await axios.patch<UserSettingsResponseDTO>(ApiConfig.USER_SETTINGS, body, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

/** Carries the HTTP status so callers can tell an expired verification token apart. */
export class SignInMethodChangeError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "SignInMethodChangeError";
    this.status = status;
  }
}

/** Step 1 of changing the sign-in method: checks the password, changes nothing. */
export const verifySignInChangePassword = async (
  accessToken: string,
  currentPassword: string,
): Promise<void> => {
  try {
    await axios.post(
      `${ApiConfig.USER_SETTINGS}/otp-preference/verify`,
      { current_password: currentPassword },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    throw new SignInMethodChangeError(
      getApiErrorMessage(error, "Unable to verify your password. Please try again."),
      error.response?.status,
    );
  }
};

/**
 * Changing otp_preference needs the CHANGE_OTP_PREFERENCE temp token from
 * verify-otp / verify-totp (not the session token) plus the current password.
 */
export const changeSignInMethod = async (
  tempAccessToken: string,
  otpPreference: OtpPreference,
  currentPassword: string,
): Promise<void> => {
  const body: UpdateUserSettingsDTO = {
    otp_preference: otpPreference,
    current_password: currentPassword,
  };
  try {
    await axios.patch<UserSettingsResponseDTO>(ApiConfig.USER_SETTINGS, body, {
      headers: { Authorization: `Bearer ${tempAccessToken}` },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    throw new SignInMethodChangeError(
      getApiErrorMessage(error, "Unable to update your sign-in method. Please try again."),
      error.response?.status,
    );
  }
};

export const userSettingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserSettings: builder.query<UserSettings, void>({
      query: () => ({ url: "/users/settings" }),
      transformResponse: (response: UserSettingsResponseDTO) =>
        mapUserSettings(response.data),
      providesTags: ["Profile"],
    }),

    updateUserSettings: builder.mutation<
      UserSettingsDataDTO,
      UpdateUserSettingsDTO
    >({
      query: (body) => ({
        url: "/users/settings",
        method: "PATCH",
        body,
      }),
      transformResponse: (response: UserSettingsResponseDTO) => response.data,
      invalidatesTags: ["Profile"],
    }),
  }),
});

export const { useGetUserSettingsQuery, useUpdateUserSettingsMutation } =
  userSettingsApi;
